import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Flex, Row, Segmented, Switch } from 'antd';
import type { IGetCargoRes } from 'api/swagger';
import { useGetManyTransports } from 'api/transports/use-get-many-transports';
import { useGetManyWarehouses } from 'api/warehouses/use-get-many-warehouses';
import type { AddressDirection } from 'types/address.ts';
import type { IPaginationInfo } from 'types/api-query.ts';
import { OrderStatus } from 'types/order';
import { TransportType } from 'types/transport.ts';
import { CargosMapList } from 'components/cargos/cargos-map-list';
import { useGetTruckTrailerPairs } from 'components/cargos/cargos-map-view/hooks/use-get-truck-trailer-pairs';
import { CargosSummary } from 'components/cargos/cargos-summary';
import { DefaultMapContainer } from 'components/map/map-container/default-map-container.tsx';
import styles from './cargos-map-view.module.scss';

interface ICargosMapViewProps {
  cargos: IGetCargoRes[];
  pagination?: IPaginationInfo | null;
}

export const CargosMapView: React.FC<ICargosMapViewProps> = (props) => {
  const { cargos, pagination } = props;
  const { t } = useTranslation();

  const nonCancelledCargos = cargos.filter((cargo) => cargo.order.status !== OrderStatus.cancelled);

  const [activeCargoId, setActiveCargoId] = useState<number | null>(null);
  const [withTransport, setWithTransport] = useState(false);
  const [costView, setCostView] = useState(false);
  const [direction, setDirection] = useState<AddressDirection>('loading');

  const { data: warehouses } = useGetManyWarehouses();
  const { data: transports, isLoading: transportIsLoading } = useGetManyTransports({
    limit: 150,
    filters: { type: TransportType.truck },
    withRefetch: true,
    isSkip: !withTransport,
  });

  const { pairs: truckTrailerPairs, isReady: pairsIsReady } = useGetTruckTrailerPairs({
    transports: transports?.data || [],
    withTransport,
  });

  const showTransport = withTransport && (!transportIsLoading || pairsIsReady);

  const handleChange = (value: string | number) => {
    setDirection(value as AddressDirection);
  };

  return (
    <>
      <Flex justify='space-between'>
        <CargosSummary />
        <Flex justify='start' align='center' gap='large' wrap>
          <Flex justify='start' align='center' gap='small'>
            Cost view:
            <Switch size='small' onChange={setCostView} />
          </Flex>
          <Flex justify='start' align='center' gap='small'>
            Transport:
            <Switch size='small' onChange={setWithTransport} />
          </Flex>
        </Flex>
      </Flex>
      <Row gutter={18}>
        <Col span={6}>
          <div className={styles.segmentedBox}>
            <Segmented
              options={[
                { label: t('cargo:loading'), value: 'loading' },
                { label: t('cargo:unloading'), value: 'unloading' },
              ]}
              onChange={handleChange}
            />
          </div>
          <CargosMapList cargos={cargos} activeCargoId={activeCargoId} pagination={pagination} direction={direction} />
        </Col>
        <Col span={18}>
          <DefaultMapContainer
            warehouses={warehouses || []}
            cargos={nonCancelledCargos}
            direction={direction}
            transports={showTransport ? transports?.data : undefined}
            truckTrailerPairs={showTransport ? truckTrailerPairs : undefined}
            handleClickShowBtn={setActiveCargoId}
            costView={costView}
          />
        </Col>
      </Row>
    </>
  );
};
